import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Rss, RefreshCw, Filter, Sparkles, XCircle, ExternalLink, Radio, Inbox,
} from "lucide-react";
import { AdminLayout } from "./AdminLayout";
import {
  adminListSources,
  adminListFeedItems,
  adminRefreshAllSources,
  adminAnalyzeFeedItem,
  adminIgnoreFeedItem,
} from "@/services/adminService";

const STATUS_META = {
  new:       { label: "جديد",         tone: "border-fursa-200 bg-fursa-50 text-fursa-700" },
  analyzing: { label: "قيد التحليل",  tone: "border-gold-200 bg-gold-100 text-gold-700"   },
  analyzed:  { label: "حُلِّل",         tone: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  ignored:   { label: "تم تجاهله",    tone: "border-surface-200 bg-surface-100 text-ink-500" },
  error:     { label: "خطأ",           tone: "border-red-200 bg-red-50 text-red-700" },
};

const TABS = [
  { key: "new",        label: "جديد" },
  { key: "analyzing",  label: "قيد التحليل" },
  { key: "analyzed",   label: "حُلِّل" },
  { key: "ignored",    label: "متجاهَل" },
  { key: "all",        label: "الكل" },
];

const formatRelative = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Date.now() - then;
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "الآن";
  if (mins < 60) return `قبل ${mins} د`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `قبل ${hrs} س`;
  const days = Math.round(hrs / 24);
  return `قبل ${days} يوماً`;
};

export default function FeedMonitorPage() {
  const [sources, setSources] = useState([]);
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pendingItemId, setPendingItemId] = useState(null);
  const [activeTab, setActiveTab] = useState("new");
  const [sourceFilter, setSourceFilter] = useState("");

  const loadAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const [srcs, its] = await Promise.all([
        adminListSources(),
        adminListFeedItems({
          status: activeTab === "all" ? undefined : activeTab,
          sourceId: sourceFilter || undefined,
          limit: 100,
        }),
      ]);
      setSources(srcs);
      setItems(its);
    } catch (e) {
      toast.error("تعذّر تحميل التغذية", { description: String(e) });
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, sourceFilter]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const refreshAll = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      const res = await adminRefreshAllSources();
      const nNew = res.results.reduce((a, r) => a + (r.new || 0), 0);
      const nErr = res.results.filter((r) => r.status === "error").length;
      if (nNew === 0 && nErr === 0) toast.info("لا بنود جديدة — كل شيء محدَّث.");
      else toast.success(`تم التحديث · ${nNew} بند جديد${nErr ? ` · ${nErr} خطأ` : ""}`);
      await loadAll();
    } catch (e) {
      toast.error("تعذّر التحديث الشامل", { description: String(e) });
    } finally {
      setIsRefreshing(false);
    }
  };

  const analyze = async (itemId) => {
    setPendingItemId(itemId);
    try {
      const res = await adminAnalyzeFeedItem(itemId);
      const jobId = res?.job?.job_id || res?.jobId;
      toast.success("أُرسِل البند إلى محرِّك التحليل", {
        description: jobId ? `Job ${String(jobId).slice(0, 8)}…` : undefined,
        action: {
          label: "متابعة في الطابور",
          onClick: () => (window.location.href = "/admin/ai-queue"),
        },
      });
      await loadAll();
    } catch (e) {
      toast.error("تعذّر إرسال البند للتحليل", { description: String(e) });
    } finally {
      setPendingItemId(null);
    }
  };

  const ignore = async (itemId) => {
    setPendingItemId(itemId);
    try {
      await adminIgnoreFeedItem(itemId);
      toast.success("تم تجاهل البند");
      await loadAll();
    } catch (e) {
      toast.error("تعذّر تجاهل البند", { description: String(e) });
    } finally {
      setPendingItemId(null);
    }
  };

  const kpi = useMemo(() => {
    const activeSources = sources.filter((s) => s.isActive).length;
    const totalItems = sources.reduce((a, s) => a + (s.itemCount || 0), 0);
    const newItems = sources.reduce((a, s) => a + (s.newCount || 0), 0);
    const errored = sources.filter((s) => s.lastFetchStatus === "error").length;
    return { activeSources, totalItems, newItems, errored };
  }, [sources]);

  return (
    <AdminLayout>
      <section className="space-y-6">
        {/* Header */}
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1.5">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-fursa-700">
              Feed Monitor · مراقبة التغذية
            </p>
            <h2 className="text-2xl font-semibold tracking-tight text-ink-900 sm:text-3xl">
              طابور المراقبة الحيّ
            </h2>
            <p className="max-w-2xl text-sm leading-relaxed text-ink-500">
              الأخبار الاقتصادية المستلَمة من مصادر RSS مُسجَّلة — راجعها ثم أرسل ما يستحق منها إلى محرِّك التحليل.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={refreshAll}
              disabled={isRefreshing || sources.length === 0}
              data-testid="feed-refresh-all"
              className="inline-flex h-10 items-center gap-1.5 rounded-lg bg-fursa-700 px-4 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-fursa-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? "animate-spin" : ""}`} strokeWidth={1.75} />
              {isRefreshing ? "جاري التحديث…" : "تحديث الآن"}
            </button>
            <Link
              to="/admin/sources"
              data-testid="feed-manage-sources"
              className="inline-flex h-10 items-center gap-1.5 rounded-lg border border-surface-200 bg-white px-3 text-xs font-semibold text-ink-700 shadow-sm transition-colors hover:border-fursa-300 hover:text-fursa-700"
            >
              <Rss className="h-3.5 w-3.5" strokeWidth={1.75} />
              إدارة المصادر
            </Link>
          </div>
        </header>

        {/* KPI strip */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <KpiCard label="مصادر نشِطة"       value={kpi.activeSources} icon={Radio} />
          <KpiCard label="إجمالي البنود"     value={kpi.totalItems}    icon={Inbox} />
          <KpiCard label="بنود جديدة"        value={kpi.newItems}      icon={Sparkles} accent />
          <KpiCard label="مصادر بأخطاء"      value={kpi.errored}       icon={XCircle}  tone={kpi.errored > 0 ? "risk" : "muted"} />
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-surface-200 bg-white p-3 shadow-card">
          <div
            data-testid="feed-tabs"
            role="tablist"
            className="inline-flex items-center gap-1 rounded-xl border border-surface-200 bg-surface-50 p-1"
          >
            {TABS.map((t) => {
              const active = t.key === activeTab;
              return (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setActiveTab(t.key)}
                  data-testid={`feed-tab-${t.key}`}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${
                    active ? "bg-fursa-700 text-white shadow-sm" : "text-ink-700 hover:bg-white"
                  }`}
                >
                  {t.key === "all" && <Filter className="h-3.5 w-3.5" strokeWidth={2} />}
                  {t.label}
                </button>
              );
            })}
          </div>
          <label className="flex items-center gap-2 text-xs">
            <span className="hidden text-[10px] font-medium uppercase tracking-widest text-ink-500 sm:inline">المصدر</span>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              data-testid="feed-source-filter"
              className="h-9 rounded-lg border border-surface-200 bg-white px-3 text-xs font-medium text-ink-900 outline-none transition-all hover:border-fursa-300 focus:border-fursa-500 focus:ring-2 focus:ring-fursa-500/15"
            >
              <option value="">كل المصادر</option>
              {sources.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </label>
          <div className="ms-auto text-xs text-ink-500">
            <span dir="ltr" className="font-mono font-bold tabular-nums text-fursa-700">{items.length}</span>{" "}
            بند
          </div>
        </div>

        {/* Items list */}
        {isLoading && items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-surface-200 bg-white p-10 text-center text-sm text-ink-500">
            جاري تحميل البنود…
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-surface-200 bg-white p-12 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-fursa-50 text-fursa-700">
              <Inbox className="h-6 w-6" strokeWidth={1.5} />
            </div>
            <h3 className="mt-4 text-lg font-semibold text-ink-900">
              {activeTab === "new"
                ? "لا بنود جديدة الآن"
                : "لا بنود ضمن هذا الفلتر"}
            </h3>
            <p className="mt-2 text-sm text-ink-500">
              {activeTab === "new"
                ? "اضغط «تحديث الآن» لسحب أحدث الأخبار من المصادر النشطة."
                : "جرّب تبويباً آخر أو مصدراً مختلفاً."}
            </p>
            {activeTab === "new" && (
              <button
                type="button"
                onClick={refreshAll}
                disabled={isRefreshing}
                className="mt-6 inline-flex items-center gap-1.5 rounded-lg bg-fursa-700 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-fursa-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <RefreshCw className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} strokeWidth={1.75} />
                تحديث الآن
              </button>
            )}
          </div>
        ) : (
          <ol data-testid="feed-items-list" className="space-y-2.5">
            {items.map((item) => {
              const meta = STATUS_META[item.status] ?? STATUS_META.new;
              const busy = pendingItemId === item.id;
              return (
                <li
                  key={item.id}
                  data-testid={`feed-item-${item.id}`}
                  className={`group rounded-xl border border-surface-200 bg-white p-4 shadow-card transition-all hover:-translate-y-0.5 hover:border-fursa-200 hover:shadow-card-hover ${busy ? "opacity-60" : ""}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 text-[11px]">
                        <span
                          data-testid={`feed-item-status-${item.id}`}
                          className={`inline-flex items-center rounded-full border px-2 py-0.5 font-semibold ${meta.tone}`}
                        >
                          {meta.label}
                        </span>
                        <span className="text-ink-500">{item.sourceName}</span>
                        <span className="text-ink-300">·</span>
                        <span className="text-ink-500">{formatRelative(item.publishedAt || item.fetchedAt)}</span>
                      </div>
                      <h3 className="mt-2 text-base font-semibold leading-snug tracking-tight text-ink-900">
                        {item.link ? (
                          <a
                            href={item.link}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="transition-colors hover:text-fursa-700"
                          >
                            {item.title}
                          </a>
                        ) : (
                          item.title
                        )}
                      </h3>
                      {item.summary && (
                        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-ink-500">
                          {item.summary}
                        </p>
                      )}
                      {item.link && (
                        <a
                          dir="ltr"
                          href={item.link}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="mt-2 inline-flex items-center gap-1 text-[11px] text-ink-500 hover:text-fursa-700"
                        >
                          <ExternalLink className="h-3 w-3" strokeWidth={1.75} />
                          فتح المصدر الأصلي
                        </a>
                      )}
                      {item.errorMessage && (
                        <p className="mt-2 text-xs text-red-700">{item.errorMessage}</p>
                      )}
                    </div>

                    <div className="flex shrink-0 flex-col items-stretch gap-2">
                      {item.status === "new" && (
                        <>
                          <button
                            type="button"
                            onClick={() => analyze(item.id)}
                            disabled={busy}
                            data-testid={`feed-item-analyze-${item.id}`}
                            className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-fursa-700 px-3 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-fursa-800 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            <Sparkles className="h-3.5 w-3.5" strokeWidth={2} />
                            حلِّل بالذكاء الاصطناعي
                          </button>
                          <button
                            type="button"
                            onClick={() => ignore(item.id)}
                            disabled={busy}
                            data-testid={`feed-item-ignore-${item.id}`}
                            className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-surface-200 bg-white px-3 text-xs font-medium text-ink-700 transition-colors hover:border-red-200 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            <XCircle className="h-3.5 w-3.5" strokeWidth={1.75} />
                            تجاهل
                          </button>
                        </>
                      )}
                      {item.status === "analyzing" && item.jobId && (
                        <Link
                          to="/admin/ai-queue"
                          className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-gold-200 bg-gold-50 px-3 text-xs font-semibold text-gold-700 hover:bg-gold-100"
                        >
                          <Sparkles className="h-3.5 w-3.5 animate-pulse" strokeWidth={2} />
                          متابعة في الطابور
                        </Link>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </section>
    </AdminLayout>
  );
}

const KpiCard = ({ label, value, icon: Icon, accent, tone }) => {
  const isRisk = tone === "risk";
  return (
    <div
      className={`rounded-xl border p-4 shadow-card ${
        accent
          ? "border-gold-200 bg-white"
          : isRisk
          ? "border-red-200 bg-white"
          : "border-surface-200 bg-white"
      }`}
    >
      <div className={`flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest ${
        accent ? "text-gold-700" : isRisk ? "text-red-700" : "text-ink-500"
      }`}>
        <Icon className={`h-3 w-3 ${accent ? "text-gold-600" : isRisk ? "text-red-600" : "text-fursa-600"}`} strokeWidth={2} />
        {label}
      </div>
      <div
        dir="ltr"
        className={`mt-2 font-mono text-2xl font-bold tabular-nums ${
          accent ? "text-gold-700" : isRisk ? "text-red-700" : "text-ink-900"
        }`}
      >
        {value}
      </div>
    </div>
  );
};
